"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, IconName } from "~/components/icons/Icon";
import { getAppCheckToken } from "~/lib/firebase-app-check";

export type Address = {
  street: string;
  city: string;
  postal_code: string;
  state: string | null;
  state_code: string | null;
  country: string | null;
  country_code: string | null;
};

export type AddressFieldProps = {
  value?: Address; // pre-populate field if backend provides
  onChange?: (address: Address) => void;
  isDisabled?: boolean;
  placeholder?: string;
  error?: boolean; // Whether to show error styling
  useRelativePositioning?: boolean; // If true, use relative positioning instead of fixed (for modals)
  dropdownWidth?: string; // Custom width for the dropdown (e.g., "w-96", "min-w-[400px]")
};

/**
 * AddressField component with Google Places autocomplete
 */
// Type for the new Places API (New) response
type AutocompleteSuggestion = {
  placePrediction: {
    placeId: string;
    text: {
      text: string;
      matches: Array<{ startOffset: number; endOffset: number }>;
    };
    structuredFormat: {
      mainText: { text: string; matches: Array<{ startOffset: number; endOffset: number }> };
      secondaryText: { text: string };
    };
    types: string[];
  };
};

const AddressField = ({ value, onChange, isDisabled, placeholder, error, useRelativePositioning = false, dropdownWidth }: AddressFieldProps) => {
  // Construct display value from address fields
  const getDisplayValue = useCallback((addr?: Address): string => {
    if (!addr) return "";
    const parts: string[] = [];
    if (addr.street) parts.push(addr.street);
    if (addr.postal_code && addr.city) {
      parts.push(`${addr.postal_code} ${addr.city}`);
    } else if (addr.city) {
      parts.push(addr.city);
    } else if (addr.postal_code) {
      parts.push(addr.postal_code);
    }
    if (addr.state) parts.push(addr.state);
    if (addr.country) parts.push(addr.country);
    return parts.join(", ");
  }, []);

  const [inputValue, setInputValue] = useState(getDisplayValue(value));
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const [suggestionsPosition, setSuggestionsPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Generate a new session token for each autocomplete session
  const generateSessionToken = useCallback(() => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }, []);

  // Update inputValue if value prop changes
  useEffect(() => {
    if (value) {
      const displayValue = getDisplayValue(value);
      if (displayValue) {
        setInputValue(displayValue);
      }
    }
  }, [value, getDisplayValue]);

  // Calculate suggestions position when they appear
  useEffect(() => {
    if (suggestions.length > 0 && inputRef.current) {
      if (useRelativePositioning) {
        // For relative positioning, we don't need to calculate position
        // The dropdown will position itself relative to the container
        setSuggestionsPosition({
          top: 0,
          left: 0,
          width: inputRef.current.offsetWidth,
        });
      } else {
        // For fixed positioning, calculate absolute position
        const rect = inputRef.current.getBoundingClientRect();
        setSuggestionsPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    } else {
      setSuggestionsPosition(null);
    }
  }, [suggestions.length, useRelativePositioning]);

  // Handle click outside to close suggestions
  useEffect(() => {
    if (suggestions.length > 0) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          inputRef.current &&
          !inputRef.current.contains(target) &&
          suggestionsRef.current &&
          !suggestionsRef.current.contains(target)
        ) {
          setSuggestions([]);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [suggestions.length]);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      // Generate new session token if not exists
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = generateSessionToken();
      }

      setLoading(true);
      try {
        const appCheckToken = await getAppCheckToken();
        const response = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "",
            "X-Goog-FieldMask":
              "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
            "Accept-Language": 'en-US',
            ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
          },
          body: JSON.stringify({
            input: query,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: [], // Empty array means all regions
          }),
        });

        if (!response.ok) {
          throw new Error(`Places API error: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error("Failed to fetch autocomplete suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [generateSessionToken]
  );

  const handleSelectSuggestion = useCallback(
    async (suggestion: AutocompleteSuggestion) => {
      setSuggestions([]);
      const placeId = suggestion.placePrediction.placeId;
      const displayText = suggestion.placePrediction.text.text;
      setInputValue(displayText);

      try {
        const appCheckToken = await getAppCheckToken();
        // Use the new Places API (New) for place details
        const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "",
            "X-Goog-FieldMask": "id,displayName,formattedAddress,location,addressComponents,plusCode",
            "Accept-Language": 'en-US',
            ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Places API error: ${response.statusText}`);
        }

        const place = await response.json();
        const newAddress: Address = {
          street: "",
          city: "",
          postal_code: "",
          state: null,
          state_code: null,
          country: null,
          country_code: null,
        };

        // Extract address components
        type AddressComponent = {
          longText: string;
          shortText: string;
          types: string[];
        };

        // First pass: collect all components for city extraction
        let localityComponent: AddressComponent | null = null;
        let postalTownComponent: AddressComponent | null = null;
        let administrativeAreaLevel2Component: AddressComponent | null = null;
        let administrativeAreaLevel3Component: AddressComponent | null = null;
        let sublocalityComponent: AddressComponent | null = null;

        place.addressComponents?.forEach((component: AddressComponent) => {
          const types = component.types || [];
          if (types.includes("locality")) {
            localityComponent = component;
          }
          if (types.includes("postal_town")) {
            postalTownComponent = component;
          }
          if (types.includes("administrative_area_level_2")) {
            administrativeAreaLevel2Component = component;
          }
          if (types.includes("administrative_area_level_3")) {
            administrativeAreaLevel3Component = component;
          }
          if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
            sublocalityComponent = component;
          }
        });

        // Extract city with smart priority logic:
        // 1. Use locality if it exists and doesn't look like a district number (e.g., "Praha 1")
        // 2. If locality has a number pattern, check administrative_area_level_2 (for Prague cases)
        // 3. Fall back to postal_town, administrative_area_level_3, administrative_area_level_2, sublocality
        if (localityComponent) {
          const localityText = (localityComponent as AddressComponent).longText;
          // Check if locality contains a number pattern like "Praha 1", "Berlin 2", etc.
          const districtNumberPattern = /^([A-Za-zÀ-ÿ\s]+?)\s+\d+$/;
          const hasDistrictNumber = districtNumberPattern.test(localityText);

          if (hasDistrictNumber) {
            const cityMatch = localityText.match(/^([A-Za-zÀ-ÿ\s]+?)(?:\s+\d+)$/);
            if (cityMatch && cityMatch[1]) {
              const extractedCity = cityMatch[1].trim();
              if (administrativeAreaLevel2Component) {
                const adminArea2Text = (administrativeAreaLevel2Component as AddressComponent).longText;
                if (adminArea2Text && !/\d/.test(adminArea2Text)) {
                  newAddress.city = adminArea2Text;
                } else {
                  newAddress.city = extractedCity;
                }
              } else {
                newAddress.city = extractedCity;
              }
            } else {
              newAddress.city = localityText;
            }
          } else {
            newAddress.city = localityText;
          }
        } else if (postalTownComponent) {
          newAddress.city = (postalTownComponent as AddressComponent).longText;
        } else if (administrativeAreaLevel3Component) {
          newAddress.city = (administrativeAreaLevel3Component as AddressComponent).longText;
        } else if (administrativeAreaLevel2Component) {
          newAddress.city = (administrativeAreaLevel2Component as AddressComponent).longText;
        } else if (sublocalityComponent) {
          newAddress.city = (sublocalityComponent as AddressComponent).longText;
        }

        // Extract other components
        let streetNumber = "";
        let route = "";

        place.addressComponents?.forEach((component: AddressComponent) => {
          const types = component.types || [];
          if (types.includes("street_number")) {
            streetNumber = component.longText;
          }
          if (types.includes("route")) {
            route = component.longText;
          }
          if (types.includes("postal_code")) {
            newAddress.postal_code = component.longText;
          }
          if (types.includes("administrative_area_level_1")) {
            newAddress.state = component.longText;
            newAddress.state_code = component.shortText;
          }
          if (types.includes("country")) {
            newAddress.country = component.longText;
            newAddress.country_code = component.shortText;
          }
        });

        // Build street: "route streetNumber" (European format: street name first, then number)
        if (route && streetNumber) {
          newAddress.street = `${route} ${streetNumber}`;
        } else if (route) {
          newAddress.street = route;
        } else if (streetNumber) {
          newAddress.street = streetNumber;
        }

        // Fallback to formatted address if street is empty (use display text)
        if (!newAddress.street && displayText) {
          newAddress.street = displayText;
        }

        // Only call onChange if we have at least city or postal_code
        if (newAddress.city || newAddress.postal_code) {
          onChange?.(newAddress);
        }

        // Consume the session token after place selection
        if (sessionTokenRef.current) {
          // Optionally, you can make a request to consume the token for billing optimization
          sessionTokenRef.current = null; // Reset for next session
        }
      } catch (error) {
        console.error("Failed to fetch place details:", error);
      }
    },
    [onChange]
  );

  return (
    <div className={twMerge("w-full", useRelativePositioning && "relative")} ref={mapRef}>
      <input
        ref={inputRef}
        type="text"
        className={twMerge(
          "w-full border border-gray-300 rounded-md px-3 h-10 text-sm focus:outline-none focus:ring-0",
          isDisabled && "bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-50 cursor-not-allowed",
          error && "border-red-500"
        )}
        placeholder={placeholder || "Enter address"}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          fetchSuggestions(e.target.value);
        }}
        disabled={isDisabled}
      />

      {/* Suggestions Dropdown */}
      {suggestions.length > 0 && suggestionsPosition && (
        <ul
          ref={suggestionsRef}
          className={twMerge(
            "z-100 bg-white shadow-lg border border-gray-200 rounded-md max-h-60 overflow-y-auto",
            useRelativePositioning ? "absolute mt-1" : "fixed",
            // Use custom dropdown width if provided, otherwise use calculated width
            dropdownWidth ? dropdownWidth : (useRelativePositioning ? "w-full" : "")
          )}
          style={useRelativePositioning
            ? undefined
            : {
              top: `${suggestionsPosition.top}px`,
              left: `${suggestionsPosition.left}px`,
              // Only set width if no custom dropdownWidth is provided
              ...(dropdownWidth ? {} : { width: `${suggestionsPosition.width}px` }),
            }}
        >
          {suggestions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            const mainText = prediction.structuredFormat?.mainText?.text || prediction.text.text;
            const secondaryText = prediction.structuredFormat?.secondaryText?.text || "";
            return (
              <li
                key={prediction.placeId || index}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <div className="font-medium">{mainText}</div>
                {secondaryText && <div className="text-xs text-gray-500">{secondaryText}</div>}
              </li>
            );
          })}
        </ul>
      )}

      {loading && (
        <div className="absolute right-2 top-2">
          <Icon name={IconName.Loader} className="animate-spin text-gray-500" />
        </div>
      )}
    </div>
  );
};

export default AddressField;
